import React, { memo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useUser } from 'Context/User';
import Uppy from '@uppy/core';
import Transloadit from '@uppy/transloadit';

import { AddInternalAtmosphericLogModal } from 'Components/RocketDry';
import {
  numberInputRegex,
  checkValidRelativeHumidity,
  checkValidTemperature,
  checkValidDewPoint,
  checkValidGPP,
  calculateDewPoint,
  calculateGPP,
} from 'Containers/RocketDry/helpers';

import { parseNumber } from 'Utils/numbers';
import { useRocketDryFunctions } from 'Context/RocketDry';
import { addRoomAtmosphericLogs } from 'Containers/RocketDry/actions';

function AddInternalAtmosphericLogModalContainer() {
  const dispatch = useDispatch();

  const {
    isAddInternalAtmosphericLogModalOpen,
    setIsAddInternalAtmosphericLogModalOpen,
    selectedRoomId,
    selectedRoomArea,
    setInternalAtmosphericLogCreated,
  }: any = useRocketDryFunctions();

  const { id: userId } = useUser();

  const [inletRelativeHumidity, setInletRelativeHumidity] = useState('');
  const [inletTemperature, setInletTemperature] = useState('');
  const [inletDewPoint, setInletDewPoint] = useState('');
  const [inletGPP, setInletGPP] = useState('');
  const [inletPhoto, setInletPhoto] = useState<File>();

  const [outletRelativeHumidity, setOutletRelativeHumidity] = useState('');
  const [outletTemperature, setOutletTemperature] = useState('');
  const [outletDewPoint, setOutletDewPoint] = useState('');
  const [outletGPP, setOutletGPP] = useState('');
  const [outletPhoto, setOutletPhoto] = useState<File>();

  const [isProcessing, setIsProcessing] = useState(false);

  const [formErrors, setFormErrors] = useState({});

  const onChangeInletRelativeHumidity = useCallback(
    ({ target: { value } }) => {
      const filtered = value.replace(numberInputRegex, '');
      setInletRelativeHumidity(filtered);

      // auto-calculate dew point and GPP
      if (filtered && inletTemperature) {
        const dewPoint = calculateDewPoint(filtered, parseNumber(inletTemperature));
        setInletDewPoint(dewPoint.toFixed(2));
        const gpp = calculateGPP(dewPoint);
        setInletGPP(gpp.toString());
      }
    },
    [inletTemperature]
  );

  const onChangeInletTemperature = useCallback(
    ({ target: { value } }) => {
      const filtered = value.replace(numberInputRegex, '');
      setInletTemperature(filtered);

      if (filtered && inletRelativeHumidity) {
        const dewPoint = calculateDewPoint(parseNumber(inletRelativeHumidity), filtered);
        setInletDewPoint(dewPoint.toFixed(2));
        const gpp = calculateGPP(dewPoint);
        setInletGPP(gpp.toString());
      }
    },
    [inletRelativeHumidity]
  );

  const onChangeInletDewPoint = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setInletDewPoint(filtered);

    // auto-calculate GPP
    if (filtered) {
      const gpp = calculateGPP(filtered);
      setInletGPP(gpp.toString());
    }
  }, []);

  const onChangeInletGPP = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setInletGPP(filtered);
  }, []);

  const onChangeOutletRelativeHumidity = useCallback(
    ({ target: { value } }) => {
      const filtered = value.replace(numberInputRegex, '');
      setOutletRelativeHumidity(filtered);

      if (filtered && outletTemperature) {
        const dewPoint = calculateDewPoint(filtered, parseNumber(outletTemperature));
        setOutletDewPoint(dewPoint.toFixed(2));
        const gpp = calculateGPP(dewPoint);
        setOutletGPP(gpp.toString());
      }
    },
    [outletTemperature]
  );

  const onChangeOutletTemperature = useCallback(
    ({ target: { value } }) => {
      const filtered = value.replace(numberInputRegex, '');
      setOutletTemperature(filtered);

      if (filtered && outletRelativeHumidity) {
        const dewPoint = calculateDewPoint(parseNumber(outletRelativeHumidity), filtered);
        setOutletDewPoint(dewPoint.toFixed(2));
        const gpp = calculateGPP(dewPoint);
        setOutletGPP(gpp.toString());
      }
    },
    [outletRelativeHumidity]
  );

  const onChangeOutletDewPoint = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setOutletDewPoint(filtered);

    if (filtered) {
      const gpp = calculateGPP(filtered);
      setOutletGPP(gpp.toString());
    }
  }, []);

  const onChangeOutletGPP = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setOutletGPP(filtered);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsAddInternalAtmosphericLogModalOpen(false);
    setInletRelativeHumidity('');
    setInletTemperature('');
    setInletDewPoint('');
    setInletGPP('');
    setInletPhoto(null);
    setOutletRelativeHumidity('');
    setOutletTemperature('');
    setOutletDewPoint('');
    setOutletGPP('');
    setOutletPhoto(null);
    setFormErrors({});
    setIsProcessing(false);
  }, []);

  const onSaveInternalButtonClick = useCallback(async () => {
    if (selectedRoomId) {
      // The process on iOS is to create both inlet and outlet logs even if one of them isn't filled out
      // this allows an easier way to update them later
      // This means we need to validate the values beforehand.
      const canCreate =
        inletRelativeHumidity ||
        inletTemperature ||
        inletDewPoint ||
        inletGPP ||
        outletRelativeHumidity ||
        outletTemperature ||
        outletDewPoint ||
        outletGPP;
      if (canCreate) {
        const errors: any = {};
        if (inletRelativeHumidity && !checkValidRelativeHumidity(parseFloat(inletRelativeHumidity))) {
          errors.inletRelativeHumidity = 'Relative humidity should be between 0 and 100.';
        }
        if (inletTemperature && !checkValidTemperature(parseFloat(inletTemperature))) {
          errors.inletTemperature = 'Temperature should be between -999 and 999.';
        }
        if (inletDewPoint && !checkValidDewPoint(parseFloat(inletDewPoint))) {
          errors.inletDewPoint = 'Dew point should be between -999 and 999.';
        }
        if (inletGPP && !checkValidGPP(parseFloat(inletGPP))) {
          errors.inletGPP = 'GPP should be between 0 and 999.';
        }

        if (outletRelativeHumidity && !checkValidRelativeHumidity(parseFloat(outletRelativeHumidity))) {
          errors.outletRelativeHumidity = 'Relative humidity should be between 0 and 100.';
        }
        if (outletTemperature && !checkValidTemperature(parseFloat(outletTemperature))) {
          errors.outletTemperature = 'Temperature should be between -999 and 999.';
        }
        if (outletDewPoint && !checkValidDewPoint(parseFloat(outletDewPoint))) {
          errors.outletDewPoint = 'Dew point should be between -999 and 999.';
        }
        if (outletGPP && !checkValidGPP(parseFloat(outletGPP))) {
          errors.outletGPP = 'GPP should be between 0 and 999.';
        }
        setFormErrors(errors);

        if (Object.keys(errors).length === 0) {
          // no errors, go ahead and create logs
          setIsProcessing(true);
          const addInletLogResponse: any = await dispatch(
            addRoomAtmosphericLogs(
              selectedRoomId,
              inletRelativeHumidity,
              inletTemperature,
              inletDewPoint,
              inletGPP,
              selectedRoomArea
            )
          );

          if (addInletLogResponse?.data) {
            const { data: inletData } = addInletLogResponse;
            const { id: inletId } = inletData;
            const addOutletLogResponse: any = await dispatch(
              addRoomAtmosphericLogs(
                selectedRoomId,
                outletRelativeHumidity,
                outletTemperature,
                outletDewPoint,
                outletGPP,
                selectedRoomArea,
                inletId
              )
            );

            if ((inletPhoto || outletPhoto) && addOutletLogResponse?.data) {
              const { data: outletData } = addOutletLogResponse;
              const { id: outletId } = outletData;

              // setup uppy
              const uppy = new Uppy().use(Transloadit, {
                waitForEncoding: true,
              });

              let filesToUpload;
              if (inletPhoto && outletPhoto) {
                filesToUpload = 2;
              } else {
                filesToUpload = 1;
              }
              let filesUploaded = 0;

              uppy.on('transloadit:complete', () => {
                filesUploaded += 1;
                if (filesUploaded === filesToUpload) {
                  // wait a bit before refetching, database needs to be synced first
                  setTimeout(() => {
                    setInternalAtmosphericLogCreated(true);
                    onCloseModal();
                  }, 1000);
                }
              });

              if (inletPhoto) {
                uppy.getPlugin('Transloadit').setOptions({
                  assemblyOptions: {
                    params: {
                      auth: { key: import.meta.env.VITE_TRANSLOADIT_AUTH_ID },
                      template_id: import.meta.env.VITE_TRANSLOADIT_ATMOSPHERICLOG_TEMPLATE_ID,
                      fields: {
                        user_id: userId,
                        source: 'webapp',
                        atmospheric_log_id: inletId,
                      },
                    },
                  },
                });
                uppy.addFile({
                  name: inletPhoto.name,
                  type: inletPhoto.type,
                  data: inletPhoto,
                });

                // upload in sequence for now. uploading in parallel results in the two photos being
                // saved to the same log
                uppy.upload().then(() => {
                  if (outletPhoto) {
                    uppy.getPlugin('Transloadit').setOptions({
                      assemblyOptions: {
                        params: {
                          auth: { key: import.meta.env.VITE_TRANSLOADIT_AUTH_ID },
                          template_id: import.meta.env.VITE_TRANSLOADIT_ATMOSPHERICLOG_TEMPLATE_ID,
                          fields: {
                            user_id: userId,
                            source: 'webapp',
                            atmospheric_log_id: outletId,
                          },
                        },
                      },
                    });
                    uppy.addFile({
                      name: outletPhoto.name,
                      type: outletPhoto.type,
                      data: outletPhoto,
                    });

                    uppy.upload();
                  }
                });
              } else if (outletPhoto) {
                uppy.getPlugin('Transloadit').setOptions({
                  assemblyOptions: {
                    params: {
                      auth: { key: import.meta.env.VITE_TRANSLOADIT_AUTH_ID },
                      template_id: import.meta.env.VITE_TRANSLOADIT_ATMOSPHERICLOG_TEMPLATE_ID,
                      fields: {
                        user_id: userId,
                        source: 'webapp',
                        atmospheric_log_id: outletId,
                      },
                    },
                  },
                });
                uppy.addFile({
                  name: outletPhoto.name,
                  type: outletPhoto.type,
                  data: outletPhoto,
                });

                uppy.upload();
              }
            } else {
              setInternalAtmosphericLogCreated(true);
              onCloseModal();
            }
          }
        }
      }
    }
  }, [
    inletRelativeHumidity,
    inletTemperature,
    inletDewPoint,
    inletGPP,
    outletRelativeHumidity,
    outletTemperature,
    outletDewPoint,
    outletGPP,
    selectedRoomId,
    selectedRoomArea,
    userId,
    inletPhoto,
    outletPhoto,
  ]);

  return (
    <AddInternalAtmosphericLogModal
      isOpen={isAddInternalAtmosphericLogModalOpen}
      closeModal={onCloseModal}
      inletRelativeHumidity={inletRelativeHumidity}
      inletTemperature={inletTemperature}
      inletDewPoint={inletDewPoint}
      inletGPP={inletGPP}
      inletPhoto={inletPhoto}
      outletRelativeHumidity={outletRelativeHumidity}
      outletTemperature={outletTemperature}
      outletDewPoint={outletDewPoint}
      outletGPP={outletGPP}
      outletPhoto={outletPhoto}
      formErrors={formErrors}
      isProcessing={isProcessing}
      onSaveInternalButtonClick={onSaveInternalButtonClick}
      onChangeInletRelativeHumidity={onChangeInletRelativeHumidity}
      onChangeInletTemperature={onChangeInletTemperature}
      onChangeInletDewPoint={onChangeInletDewPoint}
      onChangeInletGPP={onChangeInletGPP}
      setInletPhoto={setInletPhoto}
      onChangeOutletRelativeHumidity={onChangeOutletRelativeHumidity}
      onChangeOutletTemperature={onChangeOutletTemperature}
      onChangeOutletDewPoint={onChangeOutletDewPoint}
      onChangeOutletGPP={onChangeOutletGPP}
      setOutletPhoto={setOutletPhoto}
    />
  );
}

const AddInternalAtmosphericLogModalMemo = memo(AddInternalAtmosphericLogModalContainer);

export { AddInternalAtmosphericLogModalMemo as AddInternalAtmosphericLogModal };
